import consumer from "../../../channels/consumer";

export class MultiplayerManager {
  constructor(gameScene) {
    this.gameScene = gameScene;
    this.channel = null;
    this.otherPlayers = new Map();
    this.myPlayerId = null;
    this.worldId = null;
    this.lastSentPosition = { x: 0, y: 0 };
    this.lastSentTime = 0;
    this.updateFrequency = 1000 / 20; // Send updates 20 times per second
    this.isConnected = false;
    this.isPlatformMaster = false;
  }

  connect() {
    console.log('🌐 Connecting to multiplayer...');
    
    this.channel = consumer.subscriptions.create("GameChannel", {
      connected: () => {
        console.log('✅ Connected to game server!');
        this.isConnected = true;
      },

      disconnected: () => {
        console.log('❌ Disconnected from game server');
        this.isConnected = false;
        this.clearOtherPlayers();
      },

      received: (data) => {
        this.handleServerMessage(data);
      }
    });
  }

  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }
    this.isConnected = false;
    this.clearOtherPlayers();
  }

  handleServerMessage(data) {
    const { type, player_id } = data;

    // Ignore messages from ourselves
    if (this.myPlayerId && player_id === this.myPlayerId) {
      return;
    }

    switch (type) {
      case 'world_state':
        this.handleWorldState(data);
        break;
      
      case 'player_joined':
        this.handlePlayerJoined(data);
        break;
      
      case 'player_left':
        this.handlePlayerLeft(data);
        break;
      
      case 'player_update':
        this.handlePlayerUpdate(data);
        break;
      
      case 'player_jump':
        this.handlePlayerJump(data);
        break;
      
      case 'orb_collected':
        this.handleOrbCollected(data);
        break;
      
      case 'platform_sync':
        this.handlePlatformSync(data);
        break;
    }
  }

  handleWorldState(data) {
    this.myPlayerId = data.your_player_id;
    this.worldId = data.world_id;
    console.log(`🌍 Joined world ${this.worldId} as player ${this.myPlayerId}`);

    this.clearOtherPlayers();

    (data.players || []).forEach((player) => {
      if (player.player_id === this.myPlayerId) return;

      this.createOtherPlayer(player.player_id, player.player_name, player.data);
    });
  }

  handlePlayerJoined(data) {
    console.log(`👋 Player ${data.player_id} (${data.player_name}) joined the game`);
    this.createOtherPlayer(data.player_id, data.player_name, data.data);
  }

  handlePlayerLeft(data) {
    console.log(`👋 Player ${data.player_id} left the game`);
    this.removeOtherPlayer(data.player_id);
  }

  handlePlayerUpdate(data) {
    if (!this.otherPlayers.has(data.player_id)) {
      this.createOtherPlayer(data.player_id, data.player_name, data.data);
    }

    const otherPlayer = this.otherPlayers.get(data.player_id);
    if (otherPlayer && otherPlayer.sprite) {
      this.updateOtherPlayerPosition(otherPlayer, data.data);
    }
  }

  handlePlayerJump(data) {
    if (!this.otherPlayers.has(data.player_id)) {
      this.createOtherPlayer(data.player_id, data.player_name, data.data);
    }

    const otherPlayer = this.otherPlayers.get(data.player_id);
    if (otherPlayer && otherPlayer.sprite) {
      // Show jump visual effects for other players
      this.showOtherPlayerJumpEffect(otherPlayer.sprite, data.data);
    }
  }

  handleOrbCollected(data) {
    // Remove the orb from our game world when another player collects it
    this.gameScene.energyOrbs.children.entries.forEach(orb => {
      if (Math.abs(orb.x - data.orb_x) < 10 && Math.abs(orb.y - data.orb_y) < 10) {
        orb.destroy();
        this.gameScene.energyOrbs.remove(orb);
        console.log(`💎 Player ${data.player_id} collected an orb`);
      }
    });
  }

  handlePlatformSync(data) {
    // Sync platform positions if we're not the platform master
    if (!this.isPlatformMaster) {
      this.syncPlatformPositions(data.platforms);
    }
  }

  createOtherPlayer(playerId, playerName, initialData = null) {
    if (this.otherPlayers.has(playerId)) {
      return; // Player already exists
    }

    const startX = initialData?.x || 200;
    const startY = initialData?.y || 550;

    // Create a sprite for the other player
    let sprite;
    if (this.gameScene.textures.exists('frog')) {
      sprite = this.gameScene.add.sprite(startX, startY, 'frog', 'frog_grid_0');
      sprite.setScale(0.15);
      sprite.setOrigin(0.5, 0.8);
      sprite.setTint(0xFF6B6B); // Red tint to distinguish from local player
      sprite.setDepth(9); // Slightly below local player
      
      // Create animations for other players (if not already created)
      if (!this.gameScene.anims.exists('other_frog_idle')) {
        this.gameScene.anims.create({
          key: 'other_frog_idle',
          frames: [
            { key: 'frog', frame: 'frog_grid_0' },
            { key: 'frog', frame: 'frog_grid_1' }
          ],
          frameRate: 2,
          repeat: -1
        });

        this.gameScene.anims.create({
          key: 'other_frog_jump',
          frames: [
            { key: 'frog', frame: 'frog_grid_2' },
            { key: 'frog', frame: 'frog_grid_3' }
          ],
          frameRate: 8,
          repeat: 0
        });
      }
      
      sprite.play('other_frog_idle');
    } else {
      // Fallback to rectangle
      sprite = this.gameScene.add.rectangle(startX, startY, 28, 36, 0xFF6B6B);
      sprite.setDepth(9);
    }

    // Add name label
    const nameLabel = this.gameScene.add.text(0, -50, playerName || 'Player', {
      fontSize: '12px',
      fill: '#FFFFFF',
      backgroundColor: 'rgba(0,0,0,0.7)',
      padding: { x: 4, y: 2 }
    }).setOrigin(0.5);

    // Store player data
    this.otherPlayers.set(playerId, {
      sprite: sprite,
      nameLabel: nameLabel,
      lastUpdate: Date.now(),
      targetX: startX,
      targetY: startY,
      name: playerName
    });

    if (initialData) {
      this.updateOtherPlayerPosition(this.otherPlayers.get(playerId), initialData);
    }

    console.log(`🎮 Created sprite for player ${playerId}`);
  }

  removeOtherPlayer(playerId) {
    const player = this.otherPlayers.get(playerId);
    if (player) {
      player.sprite.destroy();
      player.nameLabel.destroy();
      this.otherPlayers.delete(playerId);
      console.log(`🗑️ Removed sprite for player ${playerId}`);
    }
  }

  updateOtherPlayerPosition(player, data) {
    // Smooth interpolation to the new position
    player.targetX = data.x;
    player.targetY = data.y;
    player.lastUpdate = Date.now();

    // Update animation
    if (data.animation && player.sprite.anims) {
      if (data.animation === 'frog_jump') {
        player.sprite.play('other_frog_jump');
      } else if (data.animation === 'frog_idle') {
        player.sprite.play('other_frog_idle');
      }
    }

    // Update flip
    if (data.flip_x !== undefined && player.sprite.setFlipX) {
      player.sprite.setFlipX(data.flip_x);
    }
  }

  showOtherPlayerJumpEffect(sprite, data) {
    // Create jump visual effect for other players
    const jumpEffect = this.gameScene.add.circle(sprite.x, sprite.y + 18, 12, 0xFF6B6B, 0.6);
    this.gameScene.tweens.add({
      targets: jumpEffect,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 300,
      onComplete: () => jumpEffect.destroy()
    });
  }

  clearOtherPlayers() {
    this.otherPlayers.forEach((player, playerId) => {
      this.removeOtherPlayer(playerId);
    });
  }

  // Called every frame to update other players
  update() {
    if (!this.isConnected) return;

    // Smooth movement for other players
    this.otherPlayers.forEach(player => {
      if (player.sprite && player.targetX !== undefined) {
        // Linear interpolation for smooth movement
        const lerpFactor = 0.3;
        player.sprite.x += (player.targetX - player.sprite.x) * lerpFactor;
        player.sprite.y += (player.targetY - player.sprite.y) * lerpFactor;

        // Update name label position
        player.nameLabel.x = player.sprite.x;
        player.nameLabel.y = player.sprite.y - 50;
      }
    });

    // Send our position to other players (throttled)
    this.sendPlayerUpdate();
  }

  sendPlayerUpdate() {
    if (!this.isConnected || !this.gameScene.player) return;

    const now = Date.now();
    if (now - this.lastSentTime < this.updateFrequency) return;

    const player = this.gameScene.player;
    const currentPos = { x: Math.round(player.x), y: Math.round(player.y) };

    // Only send if position changed significantly
    const distance = Math.sqrt(
      Math.pow(currentPos.x - this.lastSentPosition.x, 2) + 
      Math.pow(currentPos.y - this.lastSentPosition.y, 2)
    );

    if (distance > 5 || now - this.lastSentTime > 1000) { // Force update every second
      this.channel.perform('player_update', {
        x: currentPos.x,
        y: currentPos.y,
        velocity_x: player.body ? Math.round(player.body.velocity.x) : 0,
        velocity_y: player.body ? Math.round(player.body.velocity.y) : 0,
        animation: this.gameScene.playerType === 'frog' && player.anims.currentAnim ? 
                  player.anims.currentAnim.key : null,
        flip_x: player.flipX || false,
        on_ground: player.body ? player.body.touching.down : false
      });

      this.lastSentPosition = currentPos;
      this.lastSentTime = now;
    }
  }

  sendPlayerJump() {
    if (!this.isConnected || !this.gameScene.player) return;

    const player = this.gameScene.player;
    this.channel.perform('player_jump', {
      x: Math.round(player.x),
      y: Math.round(player.y),
      velocity_x: player.body ? Math.round(player.body.velocity.x) : 0,
      velocity_y: player.body ? Math.round(player.body.velocity.y) : 0,
      jumps_remaining: this.gameScene.jumpsRemaining
    });
  }

  sendOrbCollection(orbX, orbY) {
    if (!this.isConnected) return;

    this.channel.perform('collect_orb', {
      orb_x: Math.round(orbX),
      orb_y: Math.round(orbY)
    });
  }

  sendPlatformSync(platforms) {
    if (!this.isConnected || !this.isPlatformMaster) return;

    // Send platform positions to sync moving platforms
    const platformData = platforms.map(platform => ({
      id: platform.platformId || Math.round(platform.x * platform.y), // Simple ID
      x: Math.round(platform.x),
      y: Math.round(platform.y)
    }));

    this.channel.perform('platform_sync', {
      platforms: platformData
    });
  }

  syncPlatformPositions(platformData) {
    // Update platform positions based on server data
    platformData.forEach(data => {
      // Find matching platform by ID or position
      const platform = this.gameScene.platforms.children.entries.find(p => 
        p.platformId === data.id || 
        (Math.abs(p.platformY - data.y) < 20 && Math.abs(p.x - data.x) < 100)
      );
      
      if (platform) {
        platform.x = data.x;
        // Store ID for future reference
        platform.platformId = data.id;
      }
    });
  }
}
